import { Alert, Button, Result } from 'antd';
import Page from 'components/Page';
import Loading from 'components/Loading';
import RoutedLayout from 'components/Layout/RoutedLayout';
import { useMutation, useQuery } from 'hooks/apollo';
import useDataSource from 'hooks/useDataSource';
import useGlobalContext from 'hooks/useGlobalContext';
import useUser from 'hooks/useUser';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { EDIT_ACCESSIBILITY, EDIT_DATA_SOURCE, EDIT_LICENSE } from 'shared/constants/permissions';
import { isHiddenFindingDisposition } from 'shared/constants/accessibility/findingDisposition';
import { hasAccessibilityLicense } from 'shared/utils/license';
import { AccessibilityReviewContext } from './context';
import { EvidenceModals } from './Evidence';
import {
  GET_ACCESSIBILITY_FINDING_PAGE,
  GET_ACCESSIBILITY_REVIEW,
  GET_WEBSITE_CHECK_STATUSES,
  SET_ACCESSIBILITY_FINDING_DISPOSITION,
  SQUIDLER_FINDING_PAGE_SIZE,
} from './graphql';
import { REVIEW_CLASS, ReviewPage } from './ReviewPage';
import accessibilityRoutes from './routes';
import { applyFindingDispositionToSuites, mergeFindingSuitePages, withFindingSuites } from './utils';
import {
  PAGE_TYPE,
  includeHiddenDispositionsFromSearch,
  includeHiddenDispositionsSearch,
  pageTypeAppliesToPath,
  pageTypeForPath,
  pageTypeFromSearch,
  pageTypeSearch,
  pageTypeSyncSearch,
} from './viewModel';

const WEBSITE_CHECK_STATUS_POLL_MS = 2000;
const LIVE_WEBSITE_CHECK_REFETCH_MS = 5000;

const AccessibilityReview = () => {
  const { url: baseUrl } = useRouteMatch();
  const { pathname, search } = useLocation();
  const history = useHistory();
  const { dataSource, dataSourceId, loading: dataSourceLoading, hasDataSourcePermission } = useDataSource();
  const { hasPermission, isSuperUser } = useUser();
  const { message } = useGlobalContext();
  const hasAccessibility = isSuperUser || hasAccessibilityLicense(dataSource?.activeLicense);
  const isSettings = pathname.endsWith('/settings');
  const isScans = pathname.includes('/sync-history');
  const isPages = pathname.includes('/pages');
  const isFindings = pathname.includes('/findings');
  const isChecklist = pathname.includes('/checklist');
  const isManualReview = pathname.includes('/manual-review');
  const isOverview = !isSettings && !isScans && !isPages && !isFindings && !isChecklist && !isManualReview;
  const includeReviewResult = hasAccessibility && !isSettings && !isScans && !isOverview;
  const includeManualReview = hasAccessibility && (isChecklist || isManualReview);
  const watchWebsiteChecks = hasAccessibility && !isSettings;
  const [pageType, setPageTypeState] = useState(() => pageTypeFromSearch(search));
  const [includeHiddenDispositions, setIncludeHiddenDispositionsState] = useState(
    () => includeHiddenDispositionsFromSearch(search),
  );
  const pageTypeApplies = pageTypeAppliesToPath(pathname);
  const resultPageType = includeReviewResult && pageTypeApplies
    ? pageTypeForPath(pathname, pageType)
    : PAGE_TYPE.ALL;
  const skipReview = !dataSource?.id || !hasAccessibility;
  const shellVariables = {
    id: dataSource?.id,
    includeOps: isSuperUser && isSettings,
    includeResult: false,
    includeManualReview: false,
    includeReviewUnits: false,
    includeControls: false,
    includeScanHistory: hasAccessibility && (isPages || isScans),
    includeWebsiteChecks: hasAccessibility && isScans,
    includeWebhookDeliveries: isSuperUser && isSettings,
    includePageCounts: false,
    includeScanOverview: hasAccessibility && (isOverview || isPages || isFindings || isChecklist || isManualReview),
    includeTrend: false,
    includeSyncs: true,
    pageType: resultPageType,
  };
  const {
    data: shellData,
    loading: shellQueryLoading,
    error: shellError,
    refetch: refetchShell,
  } = useQuery(GET_ACCESSIBILITY_REVIEW, {
    variables: shellVariables,
    skip: skipReview,
  });
  const {
    data: resultData,
    loading: resultQueryLoading,
    error: resultError,
    refetch: refetchResult,
  } = useQuery(GET_ACCESSIBILITY_REVIEW, {
    variables: {
      ...shellVariables,
      includeResult: includeReviewResult,
      includeManualReview,
      includeReviewUnits: hasAccessibility && (isChecklist || isManualReview),
      includeControls: hasAccessibility && (isChecklist || isManualReview),
      includeScanHistory: false,
      includeWebsiteChecks: false,
      includeWebhookDeliveries: false,
      includePageCounts: false,
      includeScanOverview: false,
      includeTrend: false,
      includeSyncs: false,
      includeOps: false,
    },
    skip: skipReview || !includeReviewResult,
  });
  const shellLoading = shellQueryLoading && !shellData?.dataSource;
  const resultLoading = includeReviewResult && resultQueryLoading && !resultData?.dataSource;
  const reviewError = shellError || resultError;
  const refetch = useCallback(() => Promise.all([
    refetchShell(),
    includeReviewResult ? refetchResult() : Promise.resolve(),
  ]), [includeReviewResult, refetchResult, refetchShell]);
  const websiteId = dataSource?.website?.id;
  const {
    data: websiteCheckStatusData,
    startPolling: startWebsiteCheckStatusPolling,
    stopPolling: stopWebsiteCheckStatusPolling,
  } = useQuery(GET_WEBSITE_CHECK_STATUSES, {
    variables: { websiteId },
    skip: !websiteId || !hasAccessibility || !watchWebsiteChecks,
  });
  const [selectedOccurrence, setSelectedOccurrence] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
    if (!params.has('pageType')) return;
    const fromUrl = pageTypeFromSearch(search);
    setPageTypeState(current => (current === fromUrl ? current : fromUrl));
  }, [search]);

  useEffect(() => {
    const nextSearch = pageTypeSyncSearch(pathname, pageType, search);
    if (nextSearch == null) return undefined;
    const currentSearch = search || '';
    if (nextSearch === currentSearch) return undefined;
    history.replace({ pathname, search: nextSearch });
    return undefined;
  }, [history, pageType, pathname, search]);

  const setPageType = useCallback((nextPageType) => {
    setPageTypeState(nextPageType);
    if (pageTypeAppliesToPath(pathname)) {
      history.replace({ pathname, search: pageTypeSearch(nextPageType, search) });
    }
  }, [history, pathname, search]);
  const setIncludeHiddenDispositions = useCallback((nextIncludeHidden) => {
    setIncludeHiddenDispositionsState(nextIncludeHidden);
    history.replace({ pathname, search: includeHiddenDispositionsSearch(nextIncludeHidden, search) });
  }, [history, pathname, search]);

  const website = shellData?.dataSource?.website || resultData?.dataSource?.website;
  const shellScope = shellData?.dataSource?.accessibilityReviewScopes?.[0];
  const resultScope = resultData?.dataSource?.accessibilityReviewScopes?.[0];
  const scope = shellScope || resultScope
    ? {
      ...shellScope,
      ...resultScope,
      pageInventory: shellScope?.pageInventory,
      scanHistory: shellScope?.scanHistory,
      scanOverview: shellScope?.scanOverview,
      webhookDeliveries: shellScope?.webhookDeliveries ?? resultScope?.webhookDeliveries,
      webhookUrl: shellScope?.webhookUrl ?? resultScope?.webhookUrl,
      syncs: shellScope?.syncs,
      reviewResult: resultScope?.reviewResult,
    }
    : undefined;
  // Checklist and manual-review modals also link to the known automated
  // occurrences, so every result route except Overview needs the detail pages.
  const includeSquidlerFindings = includeReviewResult && !isOverview;
  const latestFindingSyncId = scope?.reviewResult?.latestCompletedSyncId
    || (scope?.syncs || []).find(sync => sync.status === 'completed')?.id
    || null;
  const [findingPageSkip, setFindingPageSkip] = useState(0);
  const [findingPageState, setFindingPageState] = useState(null);
  useEffect(() => {
    setFindingPageSkip(0);
    setFindingPageState(null);
  }, [includeSquidlerFindings, latestFindingSyncId, resultPageType, scope?.id, includeHiddenDispositions]);
  useQuery(GET_ACCESSIBILITY_FINDING_PAGE, {
    variables: {
      dataSourceId: dataSource?.id,
      syncId: latestFindingSyncId || null,
      first: SQUIDLER_FINDING_PAGE_SIZE,
      skip: findingPageSkip,
      pageType: resultPageType,
      includeHiddenDispositions,
    },
    skip: !includeSquidlerFindings || !hasAccessibility || !dataSource?.id || !scope?.id,
    fetchPolicy: 'no-cache',
    onCompleted: ({ dataSource: findingDataSource }) => {
      const page = findingDataSource?.accessibilityReviewScopes?.find(({ id }) => id === scope.id)?.findingPage;
      if (!page) return;
      if ((page.latestSyncId || null) !== (latestFindingSyncId || null)) return;
      setFindingPageState(current => ({
        syncId: page.latestSyncId || null,
        pageType: resultPageType,
        includeHiddenDispositions,
        suites: mergeFindingSuitePages(
          current?.syncId === (page.latestSyncId || null)
            && current.pageType === resultPageType
            && current.includeHiddenDispositions === includeHiddenDispositions
            ? current.suites
            : [],
          page.findingSuites,
        ),
      }));
      if (page.hasMore && page.nextSkip != null) {
        setFindingPageSkip(current => (current === findingPageSkip ? page.nextSkip : current));
      }
    },
  });
  const reviewResultWithFindings = useMemo(() => withFindingSuites(
    scope?.reviewResult,
    findingPageState?.pageType === resultPageType
      && findingPageState.includeHiddenDispositions === includeHiddenDispositions
      && (findingPageState.syncId || null) === (latestFindingSyncId || null)
      ? findingPageState.suites
      : null,
  ), [findingPageState, includeHiddenDispositions, latestFindingSyncId, resultPageType, scope?.reviewResult]);
  const result = reviewResultWithFindings;
  const websiteCheckStatuses = websiteCheckStatusData?.website?.activeChecks || (watchWebsiteChecks ? website?.checks : null);
  const brokenLinksCheckRunning = websiteCheckStatuses?.some(check => ['PENDING', 'RUNNING'].includes(check.status));
  const brokenLinksCheckWasRunning = useRef(false);
  useEffect(() => {
    if (!watchWebsiteChecks) {
      stopWebsiteCheckStatusPolling();
      return undefined;
    }
    if (brokenLinksCheckRunning) startWebsiteCheckStatusPolling(WEBSITE_CHECK_STATUS_POLL_MS);
    else stopWebsiteCheckStatusPolling();
    return () => stopWebsiteCheckStatusPolling();
  }, [
    brokenLinksCheckRunning,
    startWebsiteCheckStatusPolling,
    stopWebsiteCheckStatusPolling,
    watchWebsiteChecks,
  ]);
  useEffect(() => {
    if (!watchWebsiteChecks || !brokenLinksCheckRunning) return undefined;
    const timer = setInterval(() => {
      refetch().catch(() => { });
    }, LIVE_WEBSITE_CHECK_REFETCH_MS);
    return () => clearInterval(timer);
  }, [brokenLinksCheckRunning, refetch, watchWebsiteChecks]);
  useEffect(() => {
    if (brokenLinksCheckWasRunning.current && !brokenLinksCheckRunning) {
      refetch().catch(() => { });
    }
    brokenLinksCheckWasRunning.current = Boolean(brokenLinksCheckRunning);
  }, [brokenLinksCheckRunning, refetch]);
  const findingsLoading = Boolean(includeSquidlerFindings && !findingPageState);
  const canEditDisposition = Boolean(
    hasPermission(EDIT_ACCESSIBILITY) && hasDataSourcePermission(EDIT_DATA_SOURCE),
  );
  const [dispositionLoadingIds, setDispositionLoadingIds] = useState(() => new Set());
  const [setFindingDispositionMutation] = useMutation(SET_ACCESSIBILITY_FINDING_DISPOSITION);
  const setFindingDisposition = useCallback(async (occurrenceIds, disposition) => {
    const ids = [...new Set((occurrenceIds || []).filter(Boolean))];
    if (!ids.length || !dataSource?.id) return;
    setDispositionLoadingIds(current => new Set([...current, ...ids]));
    try {
      await setFindingDispositionMutation({
        variables: {
          dataSourceId: dataSource.id,
          occurrenceIds: ids,
          disposition,
        },
      });
      setFindingPageState(current => (current ? {
        ...current,
        suites: applyFindingDispositionToSuites(current.suites, ids, disposition, {
          includeHidden: includeHiddenDispositions,
        }),
      } : current));
      if (isHiddenFindingDisposition(disposition) && !includeHiddenDispositions) {
        refetchShell().catch(() => {});
      }
    } catch (error) {
      message.error(error.message);
    } finally {
      setDispositionLoadingIds(current => {
        const next = new Set(current);
        ids.forEach(id => next.delete(id));
        return next;
      });
    }
  }, [
    dataSource?.id,
    includeHiddenDispositions,
    message,
    refetchShell,
    setFindingDispositionMutation,
  ]);
  const contextValue = useMemo(() => ({
    baseUrl,
    website,
    scope,
    result,
    refetch,
    selectedOccurrence,
    setSelectedOccurrence,
    pageType,
    setPageType,
    includeHiddenDispositions,
    setIncludeHiddenDispositions,
    canEditDisposition,
    dispositionLoadingIds,
    setFindingDisposition,
    loading: resultLoading,
    shellLoading,
    findingsLoading,
  }), [
    baseUrl,
    canEditDisposition,
    dispositionLoadingIds,
    findingsLoading,
    includeHiddenDispositions,
    pageType,
    refetch,
    result,
    resultLoading,
    scope,
    selectedOccurrence,
    setFindingDisposition,
    setIncludeHiddenDispositions,
    setPageType,
    shellLoading,
    website,
  ]);

  if (dataSourceLoading) {
    return <Loading />;
  }

  if (!hasAccessibility) {
    return (
      <Result
        status="info"
        title="No license"
        subTitle="You need to have a license for Accessibility to view this page."
        extra={hasDataSourcePermission(EDIT_LICENSE) ? (
          <Link to={`/data-sources/${dataSourceId}/settings/license`}>
            <Button type="primary">Upgrade license</Button>
          </Link>
        ) : (
          <>Ask the owner to setup a license.</>
        )}
      />
    );
  }

  // Keep previous results on background refetch/poll failures; only block first load.
  if (reviewError && !shellData?.dataSource && !resultData?.dataSource) {
    return (
      <ReviewPage title="Accessibility">
        <Page.Content>
          <Alert
            type="error"
            showIcon
            message="Accessibility review could not be loaded."
            description="Try again later or contact an administrator if the problem remains."
          />
        </Page.Content>
      </ReviewPage>
    );
  }

  return (
    <AccessibilityReviewContext.Provider value={contextValue}>
      <RoutedLayout
        className={REVIEW_CLASS}
        sidebar
        routes={accessibilityRoutes}
      />
      <EvidenceModals />
    </AccessibilityReviewContext.Provider>
  );
};

export default AccessibilityReview;
